import { useState } from "react";
import StatusLights from "./StatusLights";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import AppointmentSummary from "./appointments/AppointmentSummary";
import { fetchAppointmentsByClientId } from "../../store/thunks/appointmentsThunk";
import { fetchInvoicesByAppointmentId } from "../../store/thunks/invoiceThunk";
import { fetchInvoicePaymentsByAppointmentId } from "../../store/thunks/paymentsThunk";

function FutureAppointments({ appointments = [] }) {
  const selectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [filter, setFilter] = useState("All"); // 'All', 'Future', 'Previous'
  const dispatch = useDispatch();

  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const duration = (endDate - startDate) / (1000 * 60); // Duration in minutes
    return duration;
  };

  function formatPrice(price) {
    if (price === undefined || price === null) {
      return "$0.00"; // Return a default value or handle it as needed
    }
    const numPrice = Number(price);
    return `$${numPrice.toFixed(2)}`;
  }

  const filteredAppointments = appointments.filter((appointment) => {
    const appointmentDate = new Date(appointment.start);
    const now = new Date();

    if (filter === "Future") {
      return appointmentDate > now;
    } else if (filter === "Previous") {
      return appointmentDate < now;
    }
    return true; // 'All' appointments
  });

  const uniqueAppointments = Array.from(
    new Set(filteredAppointments.map((a) => a._id))
  ).map((id) => filteredAppointments.find((a) => a._id === id));

  return (
    <div className="space-y-4 pt-3">
      <div className="flex flex-col space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-900">Appointments</h2>

          {/* Filter Selector */}
          <div className="flex items-center gap-x-2">
            <label className="text-xs font-medium text-gray-500">Filter:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="inline-flex h-7 items-center justify-between rounded-sm border border-gray-200 bg-white px-2 py-1 text-xs ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50">
              <option value="All">All</option>
              <option value="Future">Future</option>
              <option value="Previous">Previous</option>
            </select>
          </div>
        </div>

        <div className="rounded-sm border border-gray-100/50 bg-white shadow-sm">
          <div className="min-w-full overflow-x-auto">
            <table className="min-w-full table-fixed divide-y divide-gray-100">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="w-[30%] bg-gray-50/30 px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Appointment
                  </th>
                  <th className="w-[25%] bg-gray-50/30 px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Date & Time
                  </th>
                  <th className="w-[10%] bg-gray-50/30 px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Block #
                  </th>
                  <th className="w-[20%] bg-gray-50/30 px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Client
                  </th>
                  <th className="w-[15%] bg-gray-50/30 px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Amount
                  </th>
                  <th className="w-[15%] bg-gray-50/30 px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {uniqueAppointments.length > 0 ? (
                  uniqueAppointments.map((appointment, index) => (
                    <tr
                      key={`${appointment._id}-${index}`}
                      className="group hover:bg-gray-50/50 cursor-pointer transition-all"
                      onClick={() => {
                        dispatch(setSelectedAppointment(appointment));
                        dispatch(fetchAppointmentsByClientId(appointment.client._id));
                        dispatch(fetchInvoicesByAppointmentId(appointment._id));
                        dispatch(fetchInvoicePaymentsByAppointmentId(appointment._id));
                        setIsPanelOpen(true);
                      }}>
                      <td className="px-3 py-2">
                        <div className="truncate">
                          <p className="text-xs text-gray-900 truncate group-hover:text-gray-950">
                            {appointment.title || "N/A"}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <time dateTime={appointment.datetime} className="text-xs text-gray-500">
                          {new Date(appointment.start).toLocaleDateString("en-US", {
                            weekday: "short",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                          ,{" "}
                          {new Date(appointment.start).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </time>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-xs text-gray-900">
                          {appointment.blockNumber ? appointment.blockNumber : "N/A"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="truncate">
                          <span className="text-xs text-gray-900">
                            {appointment.client?.firstName} {appointment.client?.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-xs text-gray-900">
                          {appointment.service ? formatPrice(appointment.service.price) : "$0.00"}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <StatusLights appointment={appointment} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-3 py-6 text-center text-xs text-gray-500">
                      No {filter.toLowerCase()} appointments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isPanelOpen && (
        <AppointmentSummary
          open={isPanelOpen}
          appointment={selectedAppointment}
          onClose={() => setIsPanelOpen(false)}
        />
      )}
    </div>
  );
}

export default FutureAppointments;
